/**
 * Entitlements — what THIS company may use, as resolved by the server.
 *
 * Spec: FEATURE_MODULARIZATION_ADDONS_PLAN.md §9.2-§9.4 (catalog, presets,
 * resolver) and §9.7 (clients). The server resolver
 * (`server/src/utils/modules/resolve.js`) is the only place a plan string,
 * a Stripe add-on, a staff grant or an opt-out turns into access. It puts the
 * result on every authenticated company payload as `company.entitlements`:
 *
 *   { version: 1, preset, planKnown, modules[], keys[],
 *     limits { seats, extraSeats, products, optionsPerAttribute,
 *              inquiriesPerMonth, siteAiPerMonth, sitePages,
 *              customDomain, inquiryOnly, publishing,
 *              projectRendersPerMonth },   // null = unlimited
 *     sources, optedOut }
 *
 * and the session payload also carries `moduleCatalog` (upgrade copy: module
 * names, the lowest preset per module/key, add-on prices, preset labels),
 * which the app keeps in `state.user.moduleCatalog`, never inside the company.
 *
 * This file only READS those objects. It holds no tier table: there is no
 * plan-name → feature map here and there must never be one again (the app had
 * four and they drifted). Pure, no React, no Redux.
 *
 * Vocabulary (§4.9): a MODULE is what a company buys (`kitchen-designer`); a
 * feature KEY is the finer string a gate names (`designer-page`). Three
 * modules share their key's spelling (`visual-configurator`,
 * `carrier-services`, `b2b-portal`), so always say which you mean:
 * `hasModule(company, module)` vs `hasKey(company, key)`. Neither is an RBAC
 * permission uid.
 *
 * Fail closed: a company without entitlements (still loading, a public by-slug
 * payload — whose `entitlements` is only the storefront projection, see
 * `isEntitlements` — a stale PUT response) holds no module, no key, and the
 * Free preset's limits. Client gating is UX; the server enforces every one of these.
 */

import { isModularPricing } from '../utils/modularPricing';

export const ENTITLEMENTS_VERSION = 1;

/** `error.details.reason` on the server's coded 403 (§9.4). */
export const MODULE_NOT_AVAILABLE_REASON = 'module_not_available';
export const MODULE_NOT_AVAILABLE_NAME = 'ModuleNotAvailable';

/**
 * window CustomEvent fired by the API client on that 403; detail = { module,
 * key } plus `optedOut: true, by` when the module is turned off.
 */
export const MODULE_NOT_AVAILABLE_EVENT = 'tc:module-not-available';

/**
 * The Free preset's limits: the floor a company without entitlements reads
 * (fail closed). Mirrors server `PRESETS.free.limits`; `entitlements.test.js`
 * pins it against the generated server fixture, so a catalog change that moves
 * Free fails that test instead of drifting.
 */
export const FREE_PRESET_LIMITS = Object.freeze({
  seats: 1,
  extraSeats: false,
  products: 5,
  optionsPerAttribute: 10,
  inquiriesPerMonth: 50,
  siteAiPerMonth: 0,
  sitePages: 3,
  customDomain: false,
  inquiryOnly: true,
  publishing: true,
  projectRendersPerMonth: 0,
});

export const LIMIT_NAMES = Object.freeze(Object.keys(FREE_PRESET_LIMITS));

const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);

/** Module and feature keys compare trimmed + lowercased, like the server. */
export const normalizeKey = (raw) =>
  typeof raw === 'string' ? raw.trim().toLowerCase() : '';

// ── Reading a company ───────────────────────────────────────────────────────

/**
 * True for a FULL resolver result. A company row has no `version`; the public
 * by-slug projection (server `publicEntitlements`) has `version`, `modules`,
 * `keys` and `limits` too, but never `preset` / `planKnown`. It holds only the
 * storefront-facing modules and two limits, so reading it here would open
 * website / visual-configurator / carrier-services / b2b-portal with no core
 * keys (after create-account stores the by-slug company, or on the session
 * route's by-slug fallback). It must fail closed like a row.
 */
export const isEntitlements = (value) =>
  Boolean(
    value &&
      typeof value === 'object' &&
      value.version === ENTITLEMENTS_VERSION &&
      typeof value.preset === 'string' &&
      typeof value.planKnown === 'boolean' &&
      Array.isArray(value.modules) &&
      Array.isArray(value.keys) &&
      value.limits &&
      typeof value.limits === 'object'
  );

/**
 * The company's resolved entitlements, or null when it has none.
 * Accepts a company or an Entitlements object (like the server helpers).
 */
export const getEntitlements = (company) => {
  if (isEntitlements(company)) return company;
  const ent = company && typeof company === 'object' ? company.entitlements : null;
  return isEntitlements(ent) ? ent : null;
};

export const hasModule = (company, moduleKey) => {
  const key = normalizeKey(moduleKey);
  const ent = getEntitlements(company);
  return Boolean(key && ent && ent.modules.includes(key));
};

export const hasKey = (company, featureKey) => {
  const key = normalizeKey(featureKey);
  const ent = getEntitlements(company);
  return Boolean(key && ent && ent.keys.includes(key));
};

/**
 * A preset limit: a number, a boolean rule, or `null` for unlimited. A company
 * without entitlements (or an entitlements object missing the limit) reads the
 * Free preset's value. An unknown limit name is a programming error and throws,
 * exactly like the server.
 */
export const getLimit = (company, name) => {
  if (!LIMIT_NAMES.includes(name)) {
    throw new TypeError(
      `Unknown entitlement limit "${name}". Expected one of: ${LIMIT_NAMES.join(', ')}`
    );
  }
  const ent = getEntitlements(company);
  return ent && hasOwn(ent.limits, name) ? ent.limits[name] : FREE_PRESET_LIMITS[name];
};

export const isUnlimited = (value) => value === null;

/**
 * The Free product rule (inquiry-only storefront, no pricing / payments / quote
 * acceptance / selling-mode changes). Anything but an explicit `false` counts:
 * a company whose entitlements have not loaded is inquiry-only.
 */
export const isInquiryOnly = (company) => getLimit(company, 'inquiryOnly') !== false;

/** 'free' | 'playground' | 'base' | 'build' | 'production' | 'enterprise', or null with no entitlements. */
export const presetOf = (company) => getEntitlements(company)?.preset ?? null;

/**
 * The Playground (Self-Serve Activation): where a sign-up lands while the
 * server's PLAYGROUND_SIGNUP flag is on. A shop builds and prices products and
 * tries test orders, but cannot go live until it publishes, which starts its
 * trial. Only a RESOLVED playground answers true: a company without
 * entitlements is not one (it reads the Free floor).
 */
export const isPlayground = (company) => presetOf(company) === 'playground';

/**
 * May this company go live (public storefront, live orders, live payments)?
 * Only an explicit `publishing: false` (the Playground) says no.
 */
export const canPublish = (company) => getLimit(company, 'publishing') !== false;

/**
 * The company's stored opt-out of a module as `{ module, by }` ('staff' |
 * 'company' | 'support' | …, null when unrecorded), or null. An opted-out
 * module is not an upgrade problem: its add-on purchase is refused and the
 * company's preset may already include it.
 */
export const getOptOut = (company, moduleKey) => {
  const key = normalizeKey(moduleKey);
  const list = getEntitlements(company)?.optedOut;
  if (!key || !Array.isArray(list)) return null;
  const hit = list.find((o) => o && normalizeKey(o.module) === key);
  return hit ? { module: key, by: normalizeKey(hit.by) || null } : null;
};

/**
 * Copy for a module that is turned off rather than missing from the plan. A
 * company admin's own opt-out is theirs to undo; anyone else's (staff,
 * support, unrecorded) goes through TimberCloud support.
 */
/**
 * Modules a company Admin switches off and back on itself, whoever turned them
 * off. Mirrors COMPANY_SELF_SERVICE_MODULES in
 * server/src/api/admin/services/module-overrides.js.
 */
export const SELF_SERVICE_MODULES = Object.freeze(['carrier-services']);
export const isSelfServiceModule = (moduleKey) =>
  SELF_SERVICE_MODULES.includes(normalizeKey(moduleKey));

export const turnedOffMessage = (moduleName, by, moduleKey) => {
  const name = moduleName || 'This feature';
  if (isSelfServiceModule(moduleKey)) {
    return `${name} is turned off for your company. A company admin can turn it back on in Settings → Store → General.`;
  }
  return normalizeKey(by) === 'company'
    ? `${name} has been turned off for your company by one of your admins. Ask a company admin to turn it back on.`
    : `${name} has been turned off for your company. Contact TimberCloud support to turn it back on.`;
};

// ── Reading the catalog ─────────────────────────────────────────────────────

export const isModuleCatalog = (value) =>
  Boolean(
    value &&
      typeof value === 'object' &&
      Array.isArray(value.modules) &&
      Array.isArray(value.presets) &&
      Array.isArray(value.presetOrder)
  );

// The last catalog the app hydrated (redux/user/actions `setKeys` registers it).
// Only for the legacy helpers whose signatures take no catalog
// (`getGateMeta(key)`, `getNextPreset(null, company)`, …). New code passes
// `selectModuleCatalog(state)` explicitly.
let registeredCatalog = null;

export const registerModuleCatalog = (catalog) => {
  if (isModuleCatalog(catalog)) registeredCatalog = catalog;
  else if (catalog === null) registeredCatalog = null;
  return registeredCatalog;
};

export const getRegisteredModuleCatalog = () => registeredCatalog;

const resolveCatalog = (catalog) =>
  isModuleCatalog(catalog) ? catalog : registeredCatalog;

/** A catalog module by key, or null. */
export const getCatalogModule = (catalog, moduleKey) => {
  const cat = resolveCatalog(catalog);
  const key = normalizeKey(moduleKey);
  if (!cat || !key) return null;
  return cat.modules.find((m) => m && m.key === key) || null;
};

/** The module key that owns a feature key, or null for an unknown key. */
export const moduleForKey = (catalog, featureKey) => {
  const cat = resolveCatalog(catalog);
  const key = normalizeKey(featureKey);
  if (!cat || !key) return null;
  const mod = cat.modules.find((m) => Array.isArray(m?.keys) && m.keys.includes(key));
  return mod ? mod.key : null;
};

/**
 * Is this catalog module's add-on on sale right now? The Base add-ons
 * (`addon.listed: true`, MODULAR_SIGNUP_BASE_PLUS_ADDONS_PLAN.md §4.1) go on
 * sale with modular pricing (NEXT_PUBLIC_MODULAR_PRICING); before that only the
 * unlisted legacy add-on (the Kitchen Designer) is sold. The server refuses the
 * rest until launch too. `addon.modularOnly` (a line on the Base subscription,
 * e.g. the unlisted QuickBooks add-ons) also waits for modular pricing: the
 * server only sells those with a modular Base subscription.
 */
export const isAddonOnSale = (mod) =>
  Boolean(
    mod?.addon &&
      Array.isArray(mod.addon.purchasableOn) &&
      ((mod.addon.listed !== true && mod.addon.modularOnly !== true) || isModularPricing())
  );

/**
 * Is a preset sold today? Build and Production are grandfathered once modular
 * pricing is live (catalog `sellable: false`); before that every preset keeps
 * its old ladder.
 */
const isPresetSold = (preset) => !isModularPricing() || preset?.sellable !== false;

/**
 * `{ key, label, modules, excludedKeys, limits }` for a preset, or null. The
 * Manufacturer ladder (`presets`) first, then the Projects line
 * (`projectLine`, its own list: never part of `presetOrder`).
 */
export const getCatalogPreset = (catalog, presetKey) => {
  const cat = resolveCatalog(catalog);
  const key = normalizeKey(presetKey);
  if (!cat || !key) return null;
  return (
    cat.presets.find((p) => p && p.key === key) ||
    (Array.isArray(cat.projectLine) ? cat.projectLine.find((p) => p && p.key === key) : null) ||
    null
  );
};

/** The Projects-line presets (cheapest first), or []. */
const projectLineOf = (cat) => (cat && Array.isArray(cat.projectLine) ? cat.projectLine.filter((p) => p && p.key) : []);

// Presets the client catalog doesn't list (it lists the plan ladder only): the
// Playground and the Projects line. Their names, for a company on one.
const UNLISTED_PRESET_LABELS = { playground: 'Playground', projects: 'Projects', 'projects-pro': 'Projects Pro' };

/** A preset's display name: the catalog's label, else a known unlisted preset's, else the key capitalised. */
export const presetLabel = (catalog, presetKey) => {
  const key = normalizeKey(presetKey);
  if (!key) return null;
  return getCatalogPreset(catalog, key)?.label || UNLISTED_PRESET_LABELS[key] || key.charAt(0).toUpperCase() + key.slice(1);
};

/** Every feature key a preset grants (catalog order). */
export const presetFeatureKeys = (catalog, presetKey) => {
  const cat = resolveCatalog(catalog);
  const preset = getCatalogPreset(cat, presetKey);
  if (!preset) return [];
  const keys = [];
  for (const moduleKey of preset.modules || []) {
    const mod = getCatalogModule(cat, moduleKey);
    const excluded = preset.excludedKeys?.[moduleKey] || [];
    for (const k of mod?.keys || []) if (!excluded.includes(k)) keys.push(k);
  }
  return keys;
};

/**
 * What a preset adds for THIS company, from the catalog: the names of the
 * preset's modules the company doesn't hold yet, then its seat count. No plan
 * contents are written in the app — moving a module between presets on the
 * server changes this list. `[]` for an unknown preset or no catalog.
 *
 * @returns {string[]}
 */
export const presetHighlights = (catalog, presetKey, company) => {
  const cat = resolveCatalog(catalog);
  const preset = getCatalogPreset(cat, presetKey);
  if (!preset) return [];
  const modules = (preset.modules || [])
    .filter((key) => key !== 'core' && !hasModule(company, key))
    .map((key) => getCatalogModule(cat, key)?.name)
    .filter(Boolean);
  const seats = preset.limits?.seats;
  const seatLine =
    seats === null
      ? 'Unlimited users'
      : typeof seats === 'number'
        ? `${seats} ${seats === 1 ? 'user' : 'users'} included`
        : null;
  return seatLine ? [...modules, seatLine] : modules;
};

/**
 * The next preset above the company's, as `{ key, name }`; null at the top,
 * with no entitlements, or with no catalog.
 */
export const getNextPreset = (catalog, company) => {
  const cat = resolveCatalog(catalog);
  const current = presetOf(company);
  if (!cat || !current) return null;
  const idx = cat.presetOrder.indexOf(current);
  if (idx < 0) return null;
  // Presets that are no longer sold are never an upgrade target.
  const key = cat.presetOrder
    .slice(idx + 1)
    .find((k) => isPresetSold(getCatalogPreset(cat, k)));
  if (!key) return null;
  return { key, name: getCatalogPreset(cat, key)?.label || key };
};

/**
 * What a company needs to buy for a module or a feature key — the source of
 * every upgrade CTA's copy.
 *
 * @param {object|null} catalog `state.user.moduleCatalog` (falls back to the registered one)
 * @param {{module?: string, key?: string, company?: object, preset?: string}} target
 *   `key` wins for the lowest preset (a preset can include a module minus some
 *   of its keys, e.g. Build has Reports without `advanced-reporting`).
 *   `company` / `preset` decide the add-on framing: Kitchen Designer is an
 *   add-on for a Commerce company, an upgrade to Build for a Free one. With no
 *   known preset an add-on module keeps the add-on framing.
 *   `optedOut` / `by` carry the server deny's `details.optedOut` / `details.by`
 *   (a stale tab whose stored entitlements predate the opt-out).
 * @returns {{moduleKey: string|null, moduleName: string|null,
 *   moduleDescription: string|null, presetKey: string|null,
 *   presetLabel: string|null, isAddon: boolean, priceLabel: string|null,
 *   turnedOff: boolean, optedOutBy: string|null}}
 *   `presetKey`/`presetLabel` are null for an unknown key or a missing catalog;
 *   callers must fail closed on copy (never promise a cheaper plan).
 *   `turnedOff` means no upgrade or add-on fixes it — the module is opted out,
 *   or the company's own preset already includes it — so a CTA must say it is
 *   turned off (`turnedOffMessage`) instead of selling it; `isAddon` is then false.
 */
export const getUpgradeTarget = (
  catalog,
  { module, key, company, preset, optedOut, by } = {}
) => {
  const cat = resolveCatalog(catalog);
  const featureKey = normalizeKey(key);
  const moduleKey = normalizeKey(module) || moduleForKey(cat, featureKey) || null;
  const mod = getCatalogModule(cat, moduleKey);

  let presetKey = null;
  if (mod) {
    presetKey =
      featureKey && mod.keyLowestPreset && hasOwn(mod.keyLowestPreset, featureKey)
        ? mod.keyLowestPreset[featureKey]
        : mod.lowestPreset || null;
  }
  // The lowest preset holding it may no longer be sold (Build / Production
  // once modular pricing is live): point at the sold preset that gets it —
  // the one its add-on is bought on, else the next sold preset that includes it.
  if (cat && mod && presetKey && !isPresetSold(getCatalogPreset(cat, presetKey))) {
    const addonPreset = isAddonOnSale(mod)
      ? cat.presetOrder.find(
          (k) => mod.addon.purchasableOn.includes(k) && isPresetSold(getCatalogPreset(cat, k))
        )
      : null;
    presetKey =
      addonPreset ||
      cat.presetOrder
        .slice(cat.presetOrder.indexOf(presetKey) + 1)
        .find((k) => {
          const p = getCatalogPreset(cat, k);
          if (!isPresetSold(p)) return false;
          return featureKey ? presetFeatureKeys(cat, k).includes(featureKey) : (p?.modules || []).includes(mod.key);
        }) ||
      null;
  }

  // A shop on the Projects line upgrades within it (Projects → Projects Pro),
  // never onto the Manufacturer ladder (PROJECTS_PLANS_STRIPE_BILLING_PLAN.md
  // §4.6, §4.7): the next Projects-line preset that includes the target, else
  // nothing to sell.
  const ownPreset = normalizeKey(preset) || presetOf(company);
  const line = projectLineOf(cat);
  const lineIdx = line.findIndex((p) => p.key === ownPreset);
  if (mod && lineIdx >= 0) {
    const up = line
      .slice(lineIdx + 1)
      .find((p) => (featureKey ? presetFeatureKeys(cat, p.key).includes(featureKey) : (p.modules || []).includes(mod.key)));
    presetKey = up ? up.key : null;
  }

  // Turned off, not missing: a stored opt-out, the server deny saying so, or
  // the company's own preset granting the target while the company doesn't
  // hold it (only an opt-out takes away what the preset gives).
  const ent = getEntitlements(company);
  const optOut = getOptOut(company, moduleKey);
  const held = featureKey ? hasKey(company, featureKey) : hasModule(company, moduleKey);
  const presetIncludes = Boolean(
    ent &&
      mod &&
      !held &&
      (featureKey
        ? presetFeatureKeys(cat, ent.preset).includes(featureKey)
        : (getCatalogPreset(cat, ent.preset)?.modules || []).includes(mod.key))
  );
  const turnedOff = Boolean(optOut || optedOut || presetIncludes);

  const currentPreset = normalizeKey(preset) || presetOf(company);
  const addon = isAddonOnSale(mod) ? mod.addon : null;
  const isAddon = Boolean(
    !turnedOff && addon && (!currentPreset || addon.purchasableOn.includes(currentPreset))
  );

  return {
    moduleKey: mod ? mod.key : moduleKey,
    moduleName: mod?.name || null,
    moduleDescription: mod?.description || null,
    presetKey,
    presetLabel: presetKey ? getCatalogPreset(cat, presetKey)?.label || null : null,
    isAddon,
    // An add-on's own price on this preset (the website on Projects).
    priceLabel:
      addon && currentPreset && addon.priceOn && addon.priceOn[currentPreset] && typeof addon.priceOn[currentPreset].monthly === 'number'
        ? `$${addon.priceOn[currentPreset].monthly.toLocaleString('en-US')}/mo`
        : addon?.priceLabel || null,
    turnedOff,
    optedOutBy: turnedOff ? optOut?.by || normalizeKey(optedOut?.by ?? by) || null : null,
  };
};

// ── Writing the company into the store ──────────────────────────────────────

/**
 * Merge a company write response over the stored company without losing
 * entitlements. `PUT /companies/:id` now returns them; a response that does not
 * (an older server, a partial payload) keeps the previous company's — the
 * client never re-derives entitlements, and dropping them would flip every gate
 * closed until the next session refetch.
 */
export const mergeCompanyUpdate = (prev, next) => {
  if (!next || typeof next !== 'object') return prev;
  const base = prev && typeof prev === 'object' ? prev : {};
  const merged = { ...base, ...next };
  if (!isEntitlements(next.entitlements) && isEntitlements(base.entitlements)) {
    merged.entitlements = base.entitlements;
  }
  // The catalog lives in state.user.moduleCatalog, never on the company.
  delete merged.moduleCatalog;
  return merged;
};

/**
 * Split a `GET /company-session/:slug` payload into the company and the
 * catalog. Tolerates both `{ ...company, entitlements, moduleCatalog }` and
 * `{ company, entitlements?, moduleCatalog }`.
 */
export const splitCompanySession = (payload) => {
  if (!payload || typeof payload !== 'object') {
    return { company: payload ?? null, moduleCatalog: null };
  }
  const wrapped =
    payload.company && typeof payload.company === 'object' && payload.company.id != null;
  const source = wrapped ? payload.company : payload;
  const { moduleCatalog: innerCatalog, ...company } = source;
  if (wrapped && !isEntitlements(company.entitlements) && isEntitlements(payload.entitlements)) {
    company.entitlements = payload.entitlements;
  }
  const moduleCatalog = isModuleCatalog(payload.moduleCatalog)
    ? payload.moduleCatalog
    : isModuleCatalog(innerCatalog)
      ? innerCatalog
      : null;
  return { company, moduleCatalog };
};

const errorCandidates = (error) => [
  error?.response?.data?.error,
  error?.response?.data,
  error?.error,
  error,
];

const dispatchWindowEvent = (name, detail) => {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return;
  try {
    window.dispatchEvent(new CustomEvent(name, { detail }));
  } catch {
    // An event failure must never mask the API error itself.
  }
};

/**
 * `{ module, key }` from an API error when it is the server's coded
 * module-not-available 403, else null. Accepts an axios error, the Strapi
 * SDK's rethrown `response.data`, a parsed fetch body, or the `error` object
 * inside it. A deny for an opted-out module adds `optedOut: true` and `by`.
 */
export const readModuleNotAvailable = (error) => {
  for (const candidate of errorCandidates(error)) {
    const details = candidate?.details;
    if (details && details.reason === MODULE_NOT_AVAILABLE_REASON) {
      const deny = {
        module: normalizeKey(details.module) || null,
        key: normalizeKey(details.key) || null,
      };
      if (details.optedOut === true) {
        deny.optedOut = true;
        deny.by = normalizeKey(details.by) || null;
      }
      return deny;
    }
  }
  return null;
};

/**
 * Fire `MODULE_NOT_AVAILABLE_EVENT` when `error` is the coded module deny, so
 * `ModuleUpgradeModal` explains it. `strapi-client` does this for every
 * `strapi.axios` call; a raw `fetch` / bare `axios` caller calls it itself
 * (or `readFetchErrorBody`). Returns the deny, or null.
 */
export const announceModuleNotAvailable = (error) => {
  const deny = readModuleNotAvailable(error);
  if (deny) dispatchWindowEvent(MODULE_NOT_AVAILABLE_EVENT, deny);
  return deny;
};

/**
 * The JSON body of a failed raw `fetch` Response (`{}` when unreadable), after
 * announcing a coded module deny in it. For the `!response.ok` branch.
 */
export const readFetchErrorBody = async (response) => {
  let body = {};
  try {
    body = (await response.json()) || {};
  } catch {
    body = {};
  }
  announceModuleNotAvailable(body);
  return body;
};

/**
 * `details.reason` of the server's coded 403 for a write that needs a feature
 * key the company lacks — today the Table line field on a product save or a
 * SKU-book template (`{ reason: 'feature_required', feature: 'table-line-field' }`,
 * server `utils/table-line-field-guard.js`).
 */
export const FEATURE_REQUIRED_REASON = 'feature_required';

/**
 * `{ feature }` when an API error is the coded `feature_required` 403, else
 * null. Same inputs as `readModuleNotAvailable` (axios error, SDK-rethrown
 * body, parsed fetch body, or the inner `error`). Pair the key with
 * `getGateMeta(feature)` (util/planGate.js) for the upsell copy.
 */
export const readFeatureRequired = (error) => {
  for (const candidate of errorCandidates(error)) {
    const details = candidate?.details;
    if (details && details.reason === FEATURE_REQUIRED_REASON) {
      const feature = normalizeKey(details.feature);
      return feature ? { feature } : null;
    }
  }
  return null;
};

/** window CustomEvent for a Free-preset refusal; detail = { reason, upgradeUrl }. */
export const FREE_PLAN_BLOCKED_EVENT = 'tc:free-plan-blocked';

/**
 * `{ reason, upgradeUrl }` when an API error is a Free-preset refusal — a
 * `details.reason` starting `free_` that carries an `upgradeUrl` (company
 * `refuseModuleWrite`, the product / option / inquiry caps, the Connect and
 * selling-mode locks). Transient `free_tier_*_failed` errors carry no
 * `upgradeUrl` and are not upsells. Else null.
 */
export const readFreePlanBlocked = (error) => {
  for (const candidate of errorCandidates(error)) {
    const details = candidate?.details;
    if (
      details &&
      typeof details.reason === 'string' &&
      details.reason.startsWith('free_') &&
      typeof details.upgradeUrl === 'string'
    ) {
      return { reason: details.reason, upgradeUrl: details.upgradeUrl };
    }
  }
  return null;
};

/** Fire `FREE_PLAN_BLOCKED_EVENT` for a Free-preset refusal. Returns it, or null. */
export const announceFreePlanBlocked = (error) => {
  const blocked = readFreePlanBlocked(error);
  if (blocked) dispatchWindowEvent(FREE_PLAN_BLOCKED_EVENT, blocked);
  return blocked;
};
